// ─────────────────────────────────────────────
//  RoomStatusBreaks — the break system on the Room Status page.
//
//  Owner, 2026-09-07: "add a break system in the room status page before the
//  message box", showing the four channels and two actions — request one for
//  myself, or go and give one to somebody else.
//
//  ⚠️ PRESENTATIONAL. Every value arrives as a prop and every action leaves
//  through a callback, exactly like MobileRoomStatus itself. The break rules
//  (who may request, who may cover, what a channel state means) live in
//  useRoomBreakActions and domain/breaks, and a second opinion about them on
//  this screen is how two surfaces come to disagree about whether a room is
//  covered.
//
//  ⚠️ AND IT MOUNTS `BreakPills`, IT DOES NOT DRAW PILLS. That component owns
//  the four channel states and their colours — including the RED blink that
//  means "waiting for cover" — and is already on the room card. A second set of
//  pills here would eventually disagree with it about the same room.
// ─────────────────────────────────────────────
import React, { useState } from "react";

import mobile, { px } from "../../theme/tokens.mobile";
import { BREAK_CHANNELS } from "../../domain/breaks";
import { BreakPills, PILL_LABELS, CHANNEL_LABELS } from "./breakPieces";

const { color, space, radius, layout } = mobile;

export default function RoomStatusBreaks({
  room = null,
  // `room.breaks` is the record BreakPills reads; passed through untouched.
  canRequest = false,
  canGive = false,
  onRequest,          // (channel) => void
  onGive,             // () => void  — opens the existing Give-a-break sheet
  // ⚠️ CALLING OFF A REQUEST I RAISED MYSELF. Owner, 2026-09-11: "we have to
  // find a way that the provider can cancel the break request himself."
  // Withdrawing existed only on the web Timeline card and was gated on an AIC,
  // so a CRNA whose plans changed had no way back from their own phone and the
  // red chip sat on the board until somebody else cleared it. A request nobody
  // can retract teaches people not to raise one.
  //
  // OPTIONAL, and the control is absent without it — the hook refuses once a
  // cover has been named, and a button that refuses is worse than no button.
  onCancelRequest = null,  // () => void
  busy = false,
  // A live request already waiting for cover. While one is out, asking again is
  // not an action — it is a second alert about the same room.
  requestPending = false,
}) {
  const [picking, setPicking] = useState(false);

  // Nothing to show a break for. Absent rather than empty: a break panel on a
  // screen with no room would offer four channels nobody can be covered on.
  if (!room) return null;

  return (
    <section style={s.panel} aria-label={`${room.name} breaks`}>
      <div style={s.head}>BREAKS</div>

      {/* The four channel states, from the one component that owns them. */}
      <BreakPills breaks={room.breaks} />

      {/* ⚠️ THE TWO ACTIONS ARE DIFFERENT VERBS AND MUST LOOK IT. "Request" asks
          somebody to come to THIS room; "Give" sends me to somebody else's.
          They are opposite directions, and a single "Break" button that did
          both by context is how one word comes to mean two things. */}
      <div style={s.actions}>
        {canRequest && (
          <button
            type="button"
            data-testid="break-request"
            style={requestPending ? { ...s.btn, ...s.btnMuted } : s.btn}
            disabled={busy || requestPending}
            aria-label={
              requestPending
                ? `A break is already requested for ${room.name}`
                : `Request a break for ${room.name}`
            }
            onClick={() => setPicking((v) => !v)}
          >
            {requestPending ? "Break requested…" : "Request a break"}
          </button>
        )}
        {/* Beside the state, never instead of it: the strip has to keep SAYING
            a break is out while offering the way back. Named for the
            consequence — "Cancel request" calls off the request, which is a
            different act from calling off a COVER once somebody is walking. */}
        {canRequest && requestPending && onCancelRequest && (
          <button
            type="button"
            data-testid="break-cancel-request"
            style={s.btn}
            disabled={busy}
            aria-label={`Cancel the break request for ${room.name}`}
            onClick={() => onCancelRequest()}
          >
            Cancel request
          </button>
        )}
        {canGive && (
          <button
            type="button"
            data-testid="break-give"
            style={s.btn}
            disabled={busy}
            aria-label="Give a break to another room"
            onClick={() => onGive && onGive()}
          >
            Give a break
          </button>
        )}
      </div>

      {/* ⚠️ WHICH BREAK, ASKED SEPARATELY. Breakfast, lunch, dinner and a
          bathroom break are not interchangeable — the board schedules meal
          relief and a bathroom break is two minutes — so the request names one.
          Revealed on demand rather than always shown, because four more chips
          above the message box on a phone is the panel that pushes the case off
          the screen. */}
      {picking && canRequest && !requestPending && (
        <div style={s.picker} role="group" aria-label="Which break">
          {BREAK_CHANNELS.map((ch) => (
            <button
              key={ch}
              type="button"
              data-testid={`break-request-${ch}`}
              style={s.chip}
              disabled={busy}
              aria-label={`Request ${CHANNEL_LABELS[ch]} for ${room.name}`}
              onClick={() => {
                setPicking(false);
                if (onRequest) onRequest(ch);
              }}
            >
              {PILL_LABELS[ch]}
            </button>
          ))}
          <button
            type="button"
            style={{ ...s.chip, ...s.chipCancel }}
            onClick={() => setPicking(false)}
            aria-label="Cancel the break request"
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}

const s = {
  panel: {
    background: color.surface.ink,
    border: `1px solid ${color.border.card}`,
    borderRadius: px(radius.lg),
    padding: px(space.md),
    minWidth: 0,
    maxWidth: layout.contentMaxWidth,
    boxSizing: "border-box",
  },
  head: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    color: color.text.muted,
    textTransform: "uppercase",
  },
  actions: {
    display: "flex",
    gap: px(space.sm),
    marginTop: px(space.md),
    flexWrap: "wrap",
  },
  btn: {
    flex: "1 1 140px",
    // 44px: this is pressed one-handed, mid-case, often with gloves.
    minHeight: px(44),
    padding: `${px(10)} ${px(12)}`,
    borderRadius: px(radius.md),
    border: `1px solid ${color.border.default}`,
    // cardTop, NOT surface.card: the mobile palette has no `card` key, and an
    // undefined token in an inline style is dropped silently — the button would
    // have rendered transparent with nothing to say why.
    background: color.surface.cardTop,
    color: color.text.primary,
    font: "inherit",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  btnMuted: {
    color: color.text.muted,
    background: "transparent",
    cursor: "default",
  },
  picker: {
    display: "flex",
    gap: px(space.sm),
    marginTop: px(space.sm),
    flexWrap: "wrap",
  },
  chip: {
    minHeight: px(40),
    padding: `${px(8)} ${px(12)}`,
    borderRadius: px(radius.md),
    border: `1px solid ${color.border.default}`,
    background: "transparent",
    color: color.text.primary,
    font: "inherit",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  chipCancel: { color: color.text.muted },
};
